import { session } from "../stores/session";
import { toast } from "./toast";

export interface ApiErrorBody {
  code: string;
  message: string;
  fields?: Record<string, string>;
}

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    public body: ApiErrorBody,
  ) {
    super(body.message);
    this.name = "ApiRequestError";
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    session.clear();
    if (location.pathname !== "/login") location.assign("/login");
    throw new ApiRequestError(401, { code: "unauthenticated", message: "Session expired" });
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: ApiErrorBody } | null;
    const error = payload?.error ?? { code: "unknown", message: response.statusText };
    // 5xx is always unexpected (a specific 4xx business-rule rejection is the view's job to
    // show inline, next to the relevant action/field) — a toast is the safety net for it.
    if (response.status >= 500) {
      toast.error("Something went wrong on our end. Please try again.");
    }
    throw new ApiRequestError(response.status, error);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function commonHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (session.csrfToken.value) headers["X-CSRF-Token"] = session.csrfToken.value;
  if (session.companyId.value) headers["X-Company-Id"] = session.companyId.value;
  return headers;
}

async function doFetch(path: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(path, init);
  } catch {
    toast.error("Network error — check your connection and try again.");
    throw new ApiRequestError(0, { code: "network_error", message: "Network error" });
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const headers = { ...commonHeaders(), ...extraHeaders };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const response = await doFetch(path, {
    method,
    headers,
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  return handleResponse<T>(response);
}

async function upload<T>(path: string, file: File, method = "POST"): Promise<T> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await doFetch(path, {
    method,
    headers: commonHeaders(),
    credentials: "include",
    body: formData,
  });

  return handleResponse<T>(response);
}

async function getPaged<T>(path: string): Promise<{ items: T[]; total: number }> {
  const response = await doFetch(path, {
    method: "GET",
    headers: commonHeaders(),
    credentials: "include",
  });
  const items = await handleResponse<T[]>(response);
  const total = Number(response.headers.get("X-Total-Count") ?? items.length);
  return { items, total };
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>("GET", path),
  getPaged: <T>(path: string): Promise<{ items: T[]; total: number }> => getPaged<T>(path),
  post: <T>(path: string, body?: unknown, opts?: { headers?: Record<string, string> }): Promise<T> =>
    request<T>("POST", path, body, opts?.headers),
  put: <T>(path: string, body?: unknown): Promise<T> => request<T>("PUT", path, body),
  delete: <T>(path: string): Promise<T> => request<T>("DELETE", path),
  upload: <T>(path: string, file: File, method?: string): Promise<T> =>
    upload<T>(path, file, method),
};
